/* eslint-disable object-curly-newline */
/* global React */
import { Grid, Button } from '@mui/material'
import { isObj } from 'lib/util'
import { useMemoRef } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import LabeledText from 'ui/Element/Text/Labeled'
import Text from 'ui/Element/Text'
import OfflineHOC from 'platform/react/hoc/offline'

/**
 * A ListItemButton element with some options to configure it.
 *
 * This is a row consisting of an icon on the left, a label and value on the right, optional text
 * further to the right and a chevron at the end.
 *
 * @param {Object} forwardedRef           a reference to the root Button element
 * @param {Object} [events]               an object containing the element's event handlers
 * @param {Function} [events.onClick]     an event handler function called whenever
 *                                        the button is clicked
 * @param {String} value                  the button's text TODO: rename to label
 * @param {Boolean} [fullWidth=true]      whether the button should take parent's whole width
 * @param {Object} [props]                additional button's properties
 * @param {String} [props.icon]           the name of an icon to be displayed
 *
 * @returns {JSX.Element} a new ListItem button element
 */
const ListItemButton = ({ forwardedRef, events, fullWidth = true, ...props }) => {
  const { icon = {}, label, value, text, disabled, children, variant, color, textVariant } = props

  const handleClick = event => events.onClick(event)

  return (
    <Grid
      container
      ref={forwardedRef}
      component={Button}
      onClick={handleClick}
      alignItems={'center'}
      justifyContent={'flex-start'}
      fullWidth={fullWidth}
      flexWrap={'nowrap'}
      disabled={disabled}
      columnSpacing={1.5}
      paddingY={1.5}
      paddingX={1}
      margin={0}
    >
      <Grid
        item
        xs={'auto'}
      >
        <Icon
          source={icon}
          name={'info'}
          color={'text.secondary'}
          background={'background.dark'}
          rounded={true}
          // size={20}
        />
      </Grid>
      <Grid
        item
        container
        justifyContent={'space-between'}
        alignItems={'center'}
        flexWrap={'nowrap'}
        sx={{
          textAlign: 'left',
        }}
        xs={12}
      >
        <Grid
          item
          xs={'auto'}
        >
          <LabeledText
            label={label}
            value={value}
            autoHide={false}
            children={children}
            {...variant && isObj(variant) && { variant }}
            color={color}
            justifyContent={'flex-start'}
            alignItems={'center'}
            background={'none'}
          />
        </Grid>
        {text && (
          <Grid
            item
            component={Text}
            value={text}
            {...textVariant && { variant: textVariant }}
            xs={6}
          />
        )}
        <Grid
          item
          xs={'auto'}
          paddingX={1}
        >
          <Icon
            name={'chevron-right'}
            color={'gray.400'}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

export default useMemoRef(OfflineHOC(ListItemButton), props => [
  props.value, props.disabled, props.events, props.children,
])
